/**
 * SourceModal — Submit a source recommendation for a resource need.
 *
 * Allows a community member to provide a URL and/or explanatory note
 * to help a project find a needed resource. The recommendation goes
 * to the project owner/resource manager for review.
 */

import React, { useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { CommunityNeedItem } from '../../api/communityNeeds';
import styles from './SourceModal.module.css';

interface SourceModalProps {
  need: CommunityNeedItem;
  onSubmit: (url: string | null, note: string | null, sourceType: string) => Promise<void>;
  onClose: () => void;
}

export default function SourceModal({ need, onSubmit, onClose }: SourceModalProps) {
  const { t } = useAppTranslation('userProfile');

  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [sourceType, setSourceType] = useState<string>('external');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasContent = url.trim() !== '' || note.trim() !== '';

  const handleSubmit = useCallback(async () => {
    if (!hasContent) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(
        url.trim() || null,
        note.trim() || null,
        sourceType
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('resources.sourceSubmitError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [url, note, sourceType, hasContent, onSubmit, onClose, t]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{t('resources.sourceModalTitle')}</h3>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={styles.needContext}>
          <span className={styles.needContextLabel}>{t('resources.sourceFor')}</span>
          <span className={styles.needContextValue}>{need.label}</span>
          <span className={styles.needContextProject}>
            {need.project_name}
          </span>
        </div>

        <div className={styles.form}>
          {/* Source type */}
          <div className={styles.field}>
            <label className={styles.label}>{t('resources.sourceTypeLabel')}</label>
            <select
              className={styles.select}
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
            >
              <option value="external">{t('resources.sourceTypeExternal')}</option>
              <option value="internal">{t('resources.sourceTypeInternal')}</option>
              <option value="text">{t('resources.sourceTypeText')}</option>
            </select>
          </div>

          {/* URL field */}
          <div className={styles.field}>
            <label className={styles.label}>{t('resources.sourceUrlLabel')}</label>
            <input
              type="url"
              className={styles.input}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('resources.sourceUrlPlaceholder')}
            />
            <span className={styles.hint}>{t('resources.sourceUrlHint')}</span>
          </div>

          {/* Note field */}
          <div className={styles.field}>
            <label className={styles.label}>{t('resources.sourceNoteLabel')}</label>
            <textarea
              className={styles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('resources.sourceNotePlaceholder')}
              rows={3}
            />
          </div>

          {error && (
            <div className={styles.error}>{error}</div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
          >
            {t('resources.cancel')}
          </button>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!hasContent || isSubmitting}
          >
            {isSubmitting ? t('resources.submitting') : t('resources.submitSource')}
          </button>
        </div>
      </div>
    </div>
  );
}
